<template>
  <div class="d-flex mb-2" :class="outbound ? 'justify-content-end' : ''">
    <div class="bubble p-2 rounded" :class="bubbleClass">
      <div class="d-flex align-items-center small text-muted mb-1">
        <strong class="mr-2">{{ authorLabel }}</strong>
        <span>
          {{ message.waTimestamp || message.createdAt | moment('LLL') }}
        </span>
      </div>

      <!-- Attachment (M5): bytes fetched from Meta and stored in S3. -->
      <div v-if="message.media" class="mb-1">
        <template v-if="mediaStored">
          <a
            v-if="mediaIsImage && mediaUrl"
            :href="mediaUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img :src="mediaUrl" class="media-preview rounded border" />
          </a>
          <b-button
            v-else
            size="sm"
            variant="outline-secondary"
            :disabled="mediaOpening"
            @click="openMedia"
          >
            {{ $t('whatsapp.media.download', { type: message.contentType }) }}
          </b-button>
        </template>
        <div v-else-if="mediaFailed" class="small text-muted font-italic">
          {{ $t('whatsapp.media.unavailable') }}
        </div>
        <div v-else class="small text-muted font-italic">
          {{ $t('whatsapp.media.pending') }}
        </div>
      </div>

      <!--
        Inbound bodies are written by whoever messages our WhatsApp number.
        Interpolation escapes them; `v-html` + a markdown pass (as llama-chat
        does for our own agent's output) would hand an attacker the DOM.
      -->
      <div v-if="message.body" class="body">{{ message.body }}</div>
      <div v-else-if="sentElsewhere" class="body text-muted font-italic">
        {{ $t('whatsapp.message.sent-elsewhere') }}
      </div>
      <div v-else-if="!message.media" class="body text-muted font-italic">
        {{ $t('whatsapp.message.no-text') }}
      </div>

      <b-badge v-if="showTypeBadge" variant="secondary" class="mt-1">
        {{ message.contentType }}
      </b-badge>

      <div v-if="failed" class="small text-danger mt-1">
        {{ $t('whatsapp.message.failed') }}
        <span v-if="errorDetail">— {{ errorDetail }}</span>
      </div>
      <div v-else-if="message.status" class="small text-muted mt-1">
        {{ message.status }}
      </div>
    </div>
  </div>
</template>

<script>
import { getModule } from 'vuex-module-decorators';
import { WhatsappModule } from '@/store/whatsapp.module';

export default {
  name: 'MessageBubble',
  props: {
    message: { type: Object, required: true },
  },
  data() {
    return {
      /** Signed URL for an inline image preview; fetched lazily, cached here. */
      mediaUrl: null,
      mediaOpening: false,
    };
  },
  created() {
    this.loadImagePreview();
  },
  watch: {
    // The media processor stores the bytes seconds after the message arrives;
    // the WS re-push swaps the message prop and this flips the placeholder to
    // the real preview without a reload.
    mediaStored() {
      this.loadImagePreview();
    },
  },
  computed: {
    outbound() {
      return this.message.direction === 'outbound';
    },
    failed() {
      return this.message.status === 'failed';
    },
    isText() {
      return this.message.contentType === 'text';
    },
    mediaStored() {
      return Boolean(this.message.media && this.message.media.s3Key);
    },
    mediaFailed() {
      return this.message.media && this.message.media.fetchStatus === 'failed';
    },
    mediaIsImage() {
      return (this.message.media?.mime_type || '').startsWith('image/');
    },
    /**
     * Meta never echoes outbound content — a reply typed in Meta's WhatsApp
     * Manager reaches us as a bare delivery status, so ingest records a stub.
     * Say so, rather than showing an empty bubble that reads like a bug.
     */
    sentElsewhere() {
      return (
        this.outbound &&
        this.message.author === 'business_user' &&
        this.message.contentType === 'unknown'
      );
    },
    showTypeBadge() {
      return !this.isText && !this.sentElsewhere;
    },
    bubbleClass() {
      if (this.failed) return 'bg-white border border-danger';
      return this.outbound ? 'bg-light border' : 'bg-white border';
    },
    authorLabel() {
      const key = `whatsapp.author.${this.message.author}`;
      const label = this.$t(key);
      // vue-i18n echoes the key back when it is missing; an author value we have
      // no translation for must not render as `whatsapp.author.something`.
      return label === key ? this.message.author : label;
    },
    /**
     * Meta nests the readable text: `errors[].error_data.details` is the sentence
     * a human wants ("more than 24 hours have passed since the customer last
     * replied"), while `title` is a terse code name.
     */
    errorDetail() {
      const errors = this.message.errors;
      if (!errors) return '';
      const first = Array.isArray(errors) ? errors[0] : errors;
      if (!first) return '';
      return first.error_data?.details || first.message || first.title || '';
    },
  },
  methods: {
    store() {
      return getModule(WhatsappModule, this.$store);
    },
    async loadImagePreview() {
      if (!this.mediaStored || !this.mediaIsImage || this.mediaUrl) return;
      const result = await this.store().fetchMediaUrl({
        conversationId: this.message.conversationId,
        messageId: this.message.id,
      });
      if (result) this.mediaUrl = result.url;
    },
    /**
     * Non-image attachments open in a new tab. The signed URL lives 5 minutes,
     * so it is fetched fresh per click rather than cached at render time.
     */
    async openMedia() {
      this.mediaOpening = true;
      try {
        const result = await this.store().fetchMediaUrl({
          conversationId: this.message.conversationId,
          messageId: this.message.id,
        });
        if (result) window.open(result.url, '_blank', 'noopener');
      } finally {
        this.mediaOpening = false;
      }
    },
  },
};
</script>

<style scoped>
.bubble {
  max-width: 75%;
}
.body {
  /* WhatsApp bodies carry real newlines and long unbroken links. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.media-preview {
  max-width: 100%;
  max-height: 240px;
}
</style>
